import React, { useMemo } from "react";

import { normalizeProjectStatus } from "../utils/projectViews";

/** Approved P&L lifetime totals keyed by project id. */
export function buildPnlTotalsByProject(pnlItems) {
  const map = {};
  if (!Array.isArray(pnlItems)) return map;
  for (const row of pnlItems) {
    if (String(row?.status || "").toUpperCase() !== "APPROVED") continue;
    const pid = String(row.project ?? row.project_id ?? "");
    if (!pid) continue;
    if (!map[pid]) {
      map[pid] = { revenue: 0, expenses: 0, net: 0 };
    }
    map[pid].revenue += Number(row.revenue || 0);
    map[pid].expenses += Number(row.expenses || 0);
    map[pid].net += Number(row.net_pnl || 0);
  }
  return map;
}

export function usePnlTotalsByProject(pnlItems) {
  return useMemo(() => buildPnlTotalsByProject(pnlItems), [pnlItems]);
}

/** Compact column defs (short label + full title for tooltips). All amounts are BDT. */
export const ADMIN_PROJECT_FIN_COLUMNS = [
  { key: "funds_raised", label: "Funds", title: "Funds raised (BDT, ledger)" },
  { key: "pnl_revenue", label: "P&L Rev", title: "P&L revenue (BDT, approved)" },
  { key: "pnl_expenses", label: "P&L Exp", title: "P&L expenses (BDT, approved)" },
  { key: "net_pnl", label: "Net P&L", title: "Net P&L (BDT, approved)" },
  { key: "op_expenses", label: "Op. Exp", title: "Operational expenses (BDT, ledger)" },
  { key: "fee_income", label: "Fees", title: "Fee income (BDT, ledger)" },
  { key: "redemptions_paid", label: "Redeemed", title: "Redemptions paid (BDT, ledger)" },
  { key: "remaining_fund", label: "Remaining", title: "Remaining project fund (BDT)" },
  { key: "ultimate_pnl", label: "Ultimate", title: "Ultimate P&L (BDT, ledger)" },
];

function fmtFin(v) {
  const n = Number(v);
  if (!Number.isFinite(n)) return v == null || v === "" ? "—" : String(v);
  return `BDT ${n.toLocaleString("en-BD", { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
}

function ledgerVal(ledger, key, fallback = "0.00") {
  if (!ledger || typeof ledger !== "object") return fallback;
  const v = ledger[key];
  return v == null || v === "" ? fallback : v;
}

export function projectFinancialValues(project, pnlTotalsByProject) {
  const ledger = project?.ledger_financials || {};
  const pid = String(project?.id ?? "");
  const pnl = pnlTotalsByProject?.[pid] || {};
  const isClosed = normalizeProjectStatus(project?.status) === "CLOSED";
  return {
    funds_raised: fmtFin(ledgerVal(ledger, "total_funds_raised_bdt")),
    pnl_revenue: fmtFin(pnl.revenue ?? 0),
    pnl_expenses: fmtFin(pnl.expenses ?? 0),
    net_pnl: fmtFin(pnl.net ?? 0),
    op_expenses: fmtFin(ledgerVal(ledger, "total_accumulated_expenses_bdt")),
    fee_income: fmtFin(ledgerVal(ledger, "total_fee_income_bdt")),
    redemptions_paid: fmtFin(ledgerVal(ledger, "total_redemption_paid_bdt")),
    remaining_fund: fmtFin(ledgerVal(ledger, "remaining_project_fund_bdt")),
    ultimate_pnl: fmtFin(ledgerVal(ledger, "ultimate_pnl_bdt")),
    expensesLocked: Boolean(ledger.expenses_locked) || isClosed,
    isClosed,
  };
}

export function AdminProjectFinancialHeaderCells() {
  return ADMIN_PROJECT_FIN_COLUMNS.map((col, i) => (
    <th
      key={col.key}
      className={`admin-project-fin-th ops-table__cell--fin${i === 0 ? " admin-project-fin-th--first" : ""}${
        col.key === "ultimate_pnl" ? " admin-project-fin-th--ultimate" : ""
      }`}
      title={col.title}
    >
      {col.label}
    </th>
  ));
}

export function AdminProjectFinancialDataCells({ project, pnlTotalsByProject }) {
  const fin = projectFinancialValues(project, pnlTotalsByProject);
  return ADMIN_PROJECT_FIN_COLUMNS.map((col, i) => {
    const v = fin[col.key];
    const isUltimate = col.key === "ultimate_pnl";
    return (
      <td
        key={col.key}
        className={`admin-project-fin-td ops-table__cell--fin${i === 0 ? " admin-project-fin-td--first" : ""}${
          isUltimate ? " admin-project-fin-td--ultimate" : ""
        }`}
        title={col.title}
      >
        {isUltimate ? <strong>{v}</strong> : v}
        {isUltimate && fin.expensesLocked ? (
          <span className="admin-project-fin-locked" title="Expenses locked">
            locked
          </span>
        ) : null}
      </td>
    );
  });
}
